import { useFormContext } from 'react-hook-form';
import { Input, Select } from '@/components/common';
import { useInsurersLookup } from '@/hooks/useInsurers';
import { YesNoToggle } from '../life/YesNoToggle';
import { PREVIOUS_NCB_OPTIONS, type MotorQuoteFormData } from '../../schema';

/**
 * Prior Policy step (Wave F2 item 3a) — only rendered when Business Type is
 * Renewal or Rollover (see motorStepsForBusinessType in schema.ts; New
 * business never reaches this step at all). Captures the existing/prior
 * policy's details and wires them to QuoteRequest.previous_policy_number/
 * previous_insurer/previous_policy_expiry/previous_ncb/has_claim_in_previous
 * (backend/apps/quotes/models.py) via onMotorSubmit's vehicle payload.
 *
 * When the upfront registration-number lookup (item 3b, RegistrationNumberStep)
 * finds a matching policy/customer vehicle, it prefills these same fields
 * before the user ever reaches this step — everything below stays fully
 * editable either way.
 */
export function PriorPolicyStep() {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<MotorQuoteFormData>();

  const previousInsurerId = watch('previous_insurer_id');
  const previousNcb = watch('previous_ncb');
  const hasClaimInPrevious = watch('has_claim_in_previous');

  const { data: insurers, isLoading: insurersLoading } = useInsurersLookup();
  const insurerOptions = (insurers ?? []).map((i) => ({ value: i.id, label: i.name }));

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Prior Policy</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Tell us about the vehicle&rsquo;s existing policy so we can quote the renewal accurately.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:max-w-2xl">
        <Input
          label="Previous Policy Number"
          placeholder="e.g. POL-1234567890"
          error={errors.previous_policy_number?.message}
          required
          {...register('previous_policy_number')}
        />

        <Select
          label="Previous Insurer"
          options={insurerOptions}
          value={previousInsurerId || ''}
          onChange={(e) => {
            const insurer = (insurers ?? []).find((i) => i.id === e.target.value);
            setValue('previous_insurer_id', e.target.value, { shouldValidate: true, shouldDirty: true });
            setValue('previous_insurer_name', insurer?.name ?? '', { shouldValidate: false });
          }}
          placeholder={insurersLoading ? 'Loading...' : 'Select previous insurer'}
          error={errors.previous_insurer_id?.message}
          required
        />

        <Input
          label="Previous Policy Expiry Date"
          type="date"
          error={errors.previous_policy_expiry?.message}
          required
          {...register('previous_policy_expiry')}
        />

        <Select
          label="Previous NCB"
          options={PREVIOUS_NCB_OPTIONS}
          value={previousNcb || ''}
          onChange={(e) => setValue('previous_ncb', e.target.value, { shouldValidate: true, shouldDirty: true })}
          placeholder="Select NCB %"
        />
      </div>

      <div className="mt-4 sm:max-w-sm">
        <YesNoToggle
          label="Any claim made in the previous policy year?"
          value={hasClaimInPrevious}
          onChange={(v) => setValue('has_claim_in_previous', v, { shouldValidate: true, shouldDirty: true })}
        />
      </div>
    </div>
  );
}
